import { css } from '@emotion/core';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { CloudDownload, Code, LinkedIn, OpenInNew, Person } from '@material-ui/icons';
import React, { createContext, FC, ReactNode, useContext, useState } from 'react';

import { ProfileDataSource } from 'shared/models/person';
import { ProfileContent } from 'shared/models/profile';

import { getProfileDataSource } from '../api';
import { sanitizeHtmlForIframe } from '../common/linkedin-data';
import { fullDateTime } from '../common/timestamp';
import { RenderRawHTMLSafely } from '../components/render-raw-html-safely';
import { CurlyBracesIcon } from '../core-ui/icons/curly-braces';
import { PersonProfile } from '../sfc/person-profile';
import { useCodeModal } from './use-code-modal';
import { useHighlight } from './use-highlight';
import { useModal } from './use-modal';
import { useProfileAdornments } from './use-profile-adornment';

const styles = css`
    .MuiDialogContent-root {
        white-space: pre-wrap;
    }
`;

const nameStyles = (theme: Theme) => css`
    border: thin solid ${theme.palette.divider};
    padding: 16px 20px;
    font-size: 24px;
    font-weight: 500;
    margin-bottom: 20px;
    border-radius: ${theme.shape.borderRadius}px;
`;

const ProfileView: React.FC<{ personId: string; profile: ProfileContent; viewedAt?: number }> = ({
    personId,
    profile,
    viewedAt
}) => {
    const { pushAdornmentGenerator } = useProfileAdornments();
    const { pushHighlightTerms } = useHighlight();
    const theme = useTheme();

    // Push empty generator to clear adornments while mounted, auto-pops on unmount
    React.useEffect(() => {
        return pushAdornmentGenerator(() => ({}));
        // We intentionally want this to run only on mount/unmount for the lifetime of this component
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        return pushHighlightTerms([]);
    }, []);

    return (
        <>
            <div css={nameStyles(theme)}>{profile.fullName}</div>
            <PersonProfile profile={profile} viewedAt={viewedAt} personId={personId} />
        </>
    );
};

interface ProfileDataSourceContext {
    showProfileDataSource: () => Promise<void>;
}

const ProfileDataSourceContext = createContext<ProfileDataSourceContext>(undefined);

const ProfileDataSourceProvider: FC<{ personId: string; children: ReactNode }> = ({ children, personId }) => {
    const { showLoading, hideLoading, setAlert } = useModal();
    const { showCodeModal } = useCodeModal();
    const [data, setData] = useState<ProfileDataSource | null>();
    const [viewingProfile, setViewingProfile] = useState<{ json: ProfileContent; viewedAt?: number } | null>();
    const [viewingLinkedinHtml, setViewingLinkedinHtml] = useState<string | null>();

    const handleShowProfile = (json: ProfileContent, viewedAt?: number) => () => {
        setViewingProfile({ json, viewedAt });
    };

    const handleCloseProfile = () => {
        setViewingProfile(null);
    };

    const handleShowLinkedinData = (html: string) => () => {
        setViewingLinkedinHtml(html);
    };

    const handleCloseLinkedinData = () => {
        setViewingLinkedinHtml(null);
    };

    const handleShowJson = (title: string, json: any) => () => {
        showCodeModal(title, { language: 'json', value: json });
    };

    const handleClose = () => {
        setData(null);
    };

    const profileDialog = viewingProfile ? (
        <Dialog open={!!viewingProfile} onClose={handleCloseProfile} maxWidth="md" fullWidth={true}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    Profile Data
                </Typography>
            </DialogTitle>
            <DialogContent>
                <ProfileView profile={viewingProfile.json} personId={personId} viewedAt={viewingProfile.viewedAt} />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseProfile}>Close</Button>
            </DialogActions>
        </Dialog>
    ) : null;

    const linkedinHtmlDialog = viewingLinkedinHtml ? (
        <Dialog open={!!viewingLinkedinHtml} onClose={handleCloseLinkedinData} maxWidth="xl" fullWidth={true}>
            <DialogTitle>
                <Typography variant="h4" component="div">
                    LinkedIn Page
                </Typography>
            </DialogTitle>
            <DialogContent>
                <RenderRawHTMLSafely
                    htmlString={viewingLinkedinHtml}
                    allowedDomains={['https://*.licdn.com']}
                    preprocessHtml={sanitizeHtmlForIframe}
                    height={'calc(100vh - 200px)'}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseLinkedinData}>Close</Button>
            </DialogActions>
        </Dialog>
    ) : null;

    let resultsDialog = null;
    if (data) {
        const liData = data.parsedFromLinkedIn;
        const liSource = liData ? (
            <TableRow>
                <TableCell>
                    <Tooltip title="From LinkedIn">
                        <LinkedIn />
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title="View LinkedIn Page Data">
                        <Button onClick={handleShowLinkedinData(liData.html)}>
                            <Code />
                        </Button>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title="View Profile">
                        <span>
                            <Button disabled={!liData.json} onClick={handleShowProfile(liData.json, liData.timestamp)}>
                                <Person />
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title="View Profile JSON">
                        <span>
                            <Button disabled={!liData.json} onClick={handleShowJson('Profile JSON', liData.json)}>
                                <CurlyBracesIcon />
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Button href={data.parsedFromLinkedIn.sourceUrl} target="_blank">
                        <OpenInNew />
                    </Button>
                </TableCell>
                <TableCell>{fullDateTime(data.parsedFromLinkedIn.timestamp)}</TableCell>
            </TableRow>
        ) : null;

        const tpData = data.fetchedFromThirdParty;
        const thirdPartySource = tpData ? (
            <TableRow>
                <TableCell>
                    <Tooltip title="From 3rd party API">
                        <CloudDownload />
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title="View API JSON Response">
                        <Button onClick={handleShowJson('Third Party API Response', tpData.json)}>
                            <Code />
                        </Button>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title={tpData.parsedJson ? 'View Profile' : 'Unable to parse profile'}>
                        <span>
                            <Button
                                disabled={!tpData.parsedJson}
                                onClick={handleShowProfile(tpData.parsedJson, tpData.timestamp)}
                            >
                                <Person />
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title="View Profile JSON">
                        <span>
                            <Button
                                disabled={!tpData.parsedJson}
                                onClick={handleShowJson('Profile JSON', tpData.parsedJson)}
                            >
                                <CurlyBracesIcon />
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Button href={`https://${data.fetchedFromThirdParty.url}`} target="_blank">
                        <OpenInNew />
                    </Button>
                </TableCell>
                <TableCell>{fullDateTime(data.fetchedFromThirdParty.timestamp)}</TableCell>
            </TableRow>
        ) : null;

        const firstRow =
            data?.parsedFromLinkedIn?.timestamp > data?.fetchedFromThirdParty?.timestamp ? liSource : thirdPartySource;
        const secondRow = firstRow === liSource ? thirdPartySource : liSource;

        resultsDialog = (
            <Dialog open={!!data} onClose={handleClose} css={styles} maxWidth="lg">
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        Profile Data Source
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Table>
                        <TableBody>
                            {firstRow}
                            {secondRow}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
                {profileDialog}
                {linkedinHtmlDialog}
            </Dialog>
        );
    }

    const showProfileDataSource = async () => {
        showLoading();
        try {
            const result = await getProfileDataSource(personId);
            setData(result.data);
        } catch (e) {
            setAlert('Error', e.message ?? 'Unknown error');
        } finally {
            hideLoading();
        }
    };

    return (
        <ProfileDataSourceContext.Provider value={{ showProfileDataSource }}>
            {children}
            {resultsDialog}
        </ProfileDataSourceContext.Provider>
    );
};

const useProfileDataSource = () => {
    const context = useContext(ProfileDataSourceContext);
    if (!context) {
        throw new Error('useProfileDataSource must be used within a ProfileDataSourceProvider');
    }
    return context;
};

export { ProfileDataSourceProvider, useProfileDataSource };
